/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import clsx from 'clsx';
import { FC, useState, useEffect } from 'react';
import { Button, StepperTextBlock, getCurrentDateObject } from '@marriott/mi-ui-library';
import { useCheckBreakpoint } from '@marriott/mi-ui-library';

import { AR_LOCALE, HI_LOCALE } from '../../../../../constants/CommonConstants';
import { getLocaleFromDocument } from '../../../../../utils/CommonUtils';

import { MAX_NUMBER_OF_NIGHTS_ALLOWED } from '../../../constants/ApplicationConstants';
import { checkLocaleToAddYears } from '../../../utils/helper';

import { DatePickerFlexibleProps } from '../Calendar/index.types';
import { StyledFlexiCalendarBoxDiv } from './DatepickerFlexible.styles';
/**
 *
 * @param param0
 * // handle flexible view on the datepicker of search form
 * @returns // html element with stepper and month pill
 */

const DatePickerFlexible: FC<DatePickerFlexibleProps> = ({
  monthList,
  onDateSelection,
  clickTrackingLoc,
  singleDateLimit,
  dates,
  maxNumberOfNights = 9,
  howManyNightsText,
  isTabbedSearchForm,
  minimumStay,
  locale = 'en-US',
}) => {
  const useEnglishMomentForCalculation =
    getLocaleFromDocument() === AR_LOCALE || getLocaleFromDocument() === HI_LOCALE ? true : false;
  const currMonth = useEnglishMomentForCalculation
    ? Number(moment().locale('en').format('M')) - 1
    : Number(moment().format('M')) - 1;
  const currYear = useEnglishMomentForCalculation
    ? Number(moment().locale('en').format('YYYY'))
    : Number(moment().format('YYYY'));

  const nextYear = currYear + 1;
  const nightsLimit = maxNumberOfNights ? Number(maxNumberOfNights) : MAX_NUMBER_OF_NIGHTS_ALLOWED;
  /** component local states */
  const [nightCount, setNightCount] = useState<number>(dates?.numberOfNights || (minimumStay ?? 1)); //night count to display
  const [selectedMonth, setSelectedMonth] = useState<number>(
    useEnglishMomentForCalculation
      ? Number(moment(dates.fromDate).locale('en').format('M')) - 1
      : Number(moment(dates.fromDate).format('M')) - 1
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    useEnglishMomentForCalculation
      ? Number(moment(dates.fromDate).locale('en').format('YYYY'))
      : Number(moment(dates.fromDate).format('YYYY'))
  );
  const lastMonthToShow = useEnglishMomentForCalculation
    ? Number(moment(singleDateLimit).locale('en').format('M')) - 1
    : Number(moment(singleDateLimit).format('M')) - 1;
  const lastYeartoShow = useEnglishMomentForCalculation
    ? Number(moment(singleDateLimit).locale('en').format('YYYY'))
    : Number(moment(singleDateLimit).format('YYYY'));

  useEffect(() => {
    setNightCount(dates?.numberOfNights || (minimumStay ?? 1));
    setSelectedMonth(
      useEnglishMomentForCalculation
        ? Number(moment(dates.fromDate).locale('en').format('M')) - 1
        : Number(moment(dates.fromDate).format('M')) - 1
    );
    setSelectedYear(
      useEnglishMomentForCalculation
        ? Number(moment(dates.fromDate).locale('en').format('YYYY'))
        : Number(moment(dates.fromDate).format('YYYY'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates.fromDate, dates.numberOfNights]);

  /**
   *set from and end date on month selection
   * and night count
   * <month index>, nightcount or length stay
   */
  const updateDateOnDateSelection = (month: number, year: number, nightCount = 1) => {
    let startDate = moment().month(month).startOf('month');
    startDate = year === nextYear ? startDate.add(1, 'y') : startDate; // add one year if we have selected next year
    startDate = startDate.isBefore(getCurrentDateObject()) ? getCurrentDateObject() : startDate;

    const endDate = moment(startDate.toDate()).add(nightCount, 'days');
    onDateSelection(startDate, endDate, true); // set date in case of user switch the tabs
  };
  const onMonthSelection = (monthInd: number, year: number): void => {
    /**
     * on month selection update the year and month
     */
    setSelectedYear(year);
    setSelectedMonth(monthInd);
    updateDateOnDateSelection(monthInd, year, nightCount);
  };

  const onNightCountAdd = (): void => {
    /**
     * on update night count
     */
    const nightsCountInFlexibleSearch = nightCount < Number(nightsLimit) ? nightCount + 1 : Number(nightsLimit);
    setNightCount(nightsCountInFlexibleSearch);
    updateDateOnDateSelection(selectedMonth, selectedYear, nightsCountInFlexibleSearch);
  };

  const onNightCountDecr = (): void => {
    const nightsCountInFlexibleSearch = nightCount <= 1 ? 1 : nightCount - 1;
    setNightCount(nightsCountInFlexibleSearch);
    updateDateOnDateSelection(selectedMonth, selectedYear, nightsCountInFlexibleSearch);
  };

  const viewportL = useCheckBreakpoint('viewportL');

  const MonthPill = (index: number, month: string, year: number) => (
    /**
     * reusable button used to render month pill in the flxible section
     */
    <Button
      key={`${index}-${month}-${year}`}
      testId={`${index}-button`}
      ariaExpanded={selectedMonth === index && selectedYear === year}
      className={clsx(
        'custom_click_track',
        'm-button-s',
        'mb-2 mr-2',
        selectedMonth === index && selectedYear === year
          ? 'selected-month m-button-primary-inverse '
          : 'm-button-secondary default-month-state',
        'month-pill'
      )}
      {...{
        custom_click_track_value: `${clickTrackingLoc}| Flexible Date Picker Month Selected ${month} button |internal`,
      }}
      callback={(): void => onMonthSelection(index, year)}
    >
      <span className="d-inline">{month}</span>
    </Button>
  );

  return (
    <StyledFlexiCalendarBoxDiv className="search__flexible_calendar pb-5 pb-lg-0" data-test-id="flexible">
      <div className={clsx('stepper-wrapper')}>
        <div className="weekend-selector-horizontal-line-flexible mt-2 mb-4 d-lg-none"></div>
        <StepperTextBlock
          copyText={howManyNightsText}
          stepperValue={nightCount}
          handleIncrement={onNightCountAdd}
          handleDecrement={onNightCountDecr}
          disableDecrement={nightCount === minimumStay}
          disableIncrement={nightCount === nightsLimit}
          decreamentLabel="night count decrease"
          increamentLabel="night count increase"
          clickTrackloc={`${clickTrackingLoc}  Flexible| night count`}
        />
      </div>
      <div className={clsx('months-pill-container', 'pb-5 mb-5 mb-lg-0', !isTabbedSearchForm && 'months-pill-spacing')}>
        <div className="t-font-alt-xs display-year">{checkLocaleToAddYears(locale, currYear)}</div>
        <div className="d-flex flex-wrap mx-0 py-2">
          {monthList?.map((month, index) => {
            // index >= (lastYeartoShow === currYear ? lastMonthToShow : currMonth)
            return lastYeartoShow === currYear
              ? index <= lastMonthToShow && index >= currMonth && MonthPill(index, month, currYear)
              : index >= currMonth && MonthPill(index, month, currYear);
          })}
        </div>
        {nextYear <= lastYeartoShow && (
          <>
            <div className={clsx('t-font-alt-xs', 'display-year', viewportL && 'pt-2')}>
              {checkLocaleToAddYears(locale, nextYear)}
            </div>
            <div className={clsx('d-flex', 'flex-wrap', ' mx-0', ' py-2')}>
              {monthList?.map((month, index) => {
                return index <= lastMonthToShow && MonthPill(index, month, nextYear);
              })}
            </div>
          </>
        )}
      </div>
    </StyledFlexiCalendarBoxDiv>
  );
};

export default DatePickerFlexible;
